import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { ChatResponse, KeyFact } from '../types';
import ConfidencePill from './ConfidencePill';
import KeyFactCards from './KeyFactCards';
import AnswerTable from './AnswerTable';
import ClarificationChips from './ClarificationChips';
import FollowUpSuggestion from './FollowUpSuggestion';
import CopyButton from './CopyButton';

interface Props {
  data: ChatResponse;
  onClarificationSelect: (option: string) => void;
  onFollowUpClick: (text: string) => void;
  onViewEvidence: (messageId: string, highlightNodeId?: string | null) => void;
  messageId: string;
  isEvidenceOpen: boolean;
  busy: boolean;
}

export default function ChatMessage({
  data,
  onClarificationSelect,
  onFollowUpClick,
  onViewEvidence,
  messageId,
  isEvidenceOpen,
  busy,
}: Props) {
  const [answerExpanded, setAnswerExpanded] = useState(false);

  // Cypher alone counts as evidence: scalar-projection queries (Recipe B1/E/F)
  // return empty `nodes` + `relationships` by design but still ship a runnable
  // Cypher string — the panel must surface it so users can paste-and-verify.
  const hasEvidence =
    data.evidence.nodes.length > 0 ||
    data.evidence.relationships.length > 0 ||
    !!data.evidence.cypher;

  const trulyNoEvidence =
    data.evidence.nodes.length === 0 &&
    data.evidence.relationships.length === 0 &&
    !data.evidence.cypher;

  const evidenceCountLabel = (() => {
    const { nodes, relationships, cypher } = data.evidence;
    if (nodes.length > 0) {
      return `${nodes.length} ${nodes.length === 1 ? 'node' : 'nodes'}`;
    }
    if (relationships.length > 0) {
      return `${relationships.length} ${
        relationships.length === 1 ? 'relationship' : 'relationships'
      }`;
    }
    return cypher ? 'Cypher' : '';
  })();

  // Fallback: if `summary` is empty, use the first non-empty line of `answer`
  // as the headline (per §3.1 rendering recipe).
  const headline =
    data.summary && data.summary.trim()
      ? data.summary
      : (data.answer || '').split('\n').find((l) => l.trim()) || '';

  // The structured `summary` already covers the headline. To avoid duplicating
  // it, hide the answer block by default when it starts with the same sentence
  // and is longer than ~280 chars — show a "Read more" toggle instead.
  const answer = data.answer || '';
  const longAnswer = answer.length > 280;
  const showAnswer = !longAnswer || answerExpanded;

  const handleKeyFactClick = (fact: KeyFact) => {
    onViewEvidence(messageId, fact.source_node_id);
  };

  // Mode B (answer) only: render the canonical `suggested_questions` as
  // follow-up pills, falling back to the deprecated single `follow_up` mirror
  // when the array is absent. Clarification turns carry no suggestions.
  const suggestions =
    !data.clarification_required
      ? data.suggested_questions && data.suggested_questions.length > 0
        ? data.suggested_questions
        : data.follow_up
          ? [data.follow_up]
          : []
      : [];

  // Plain-text rendering of the answer for "Copy response" — headline +
  // narrative + the key-fact figures, de-duplicated so we don't repeat the
  // summary when the answer already opens with it.
  const responseText = (() => {
    const parts: string[] = [];
    const summary = (data.summary || '').trim();
    const body = (data.answer || '').trim();
    if (summary) parts.push(summary);
    if (body && body !== summary) parts.push(body);
    if (data.key_facts.length > 0) {
      parts.push(data.key_facts.map((f) => `• ${f.label}: ${f.value}`).join('\n'));
    }
    return parts.join('\n\n');
  })();

  return (
    <article className="bg-white border border-neutral-200 rounded-2xl rounded-tl-sm shadow-sm p-5 md:p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-2 flex-wrap">
        <ConfidencePill confidence={data.confidence} />
        {trulyNoEvidence && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
            <AlertTriangle className="w-3 h-3" /> No evidence found
          </span>
        )}
      </div>

      {headline && (
        <h3 className="text-base md:text-lg font-bold text-neutral-900 leading-snug">
          {headline}
        </h3>
      )}

      {data.key_facts.length > 0 && (
        <KeyFactCards facts={data.key_facts} onFactClick={handleKeyFactClick} />
      )}

      {data.table && <AnswerTable table={data.table} />}

      {answer && (
        <div className="space-y-1">
          <div
            className={`max-w-none text-sm text-neutral-700 leading-relaxed [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-semibold [&_strong]:text-neutral-900 [&_a]:text-primary-600 [&_a]:underline [&_code]:font-mono [&_code]:text-[0.85em] [&_code]:bg-neutral-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded ${
              showAnswer ? '' : 'line-clamp-3'
            }`}
          >
            <ReactMarkdown disallowedElements={['script']}>{answer}</ReactMarkdown>
          </div>
          {longAnswer && (
            <button
              type="button"
              onClick={() => setAnswerExpanded((e) => !e)}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              {answerExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" /> Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" /> Read more
                </>
              )}
            </button>
          )}
        </div>
      )}

      {data.clarification_required && data.options.length > 0 && (
        <ClarificationChips
          options={data.options}
          onSelect={onClarificationSelect}
          disabled={busy}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
        {hasEvidence && (
          <button
            type="button"
            onClick={() => onViewEvidence(messageId)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
              isEvidenceOpen
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
            aria-expanded={isEvidenceOpen}
          >
            {isEvidenceOpen ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
            View evidence{evidenceCountLabel && ` (${evidenceCountLabel})`}
          </button>
        )}
        {responseText && (
          <CopyButton
            text={responseText}
            label="Copy"
            title="Copy response"
            className="border border-neutral-200 bg-white"
          />
        )}
        {suggestions.map((q) => (
          <FollowUpSuggestion
            key={q}
            text={q}
            onClick={onFollowUpClick}
            disabled={busy}
          />
        ))}
      </div>
    </article>
  );
}
